// src/services/byodBands.ts
// -------------------------
// PURE band-resolution helpers for the BYOD Stage-3 UI (db-198/db-199). No IO, so
// unit-tested directly (src/services/byodBands.test.ts). Given a child's numeric
// year level (yearLevelToNumber) and a school's active bands, decide which band
// applies and whether it has gone stale.

import { policyYearFor } from "../../supabase/functions/_shared/byod/parseByodResult";

// The subset of a school_byod row these pure helpers need. The full row type is
// ByodBand (src/data/schoolByod.ts); this keeps the helpers trivially testable.
export interface BandLike {
  year_from: number | null;
  year_to: number | null;
  deactivated?: boolean | null;
  byod_policy_year?: number | null;
  byod_checked_at?: string | null;
}

// The ACTIVE band covering `yearNum` (Prep=0..Year 12=12). When several cover it,
// prefer the NARROWEST span (a specific primary/secondary band wins over a
// whole-school one), then the most recently checked. Returns null when yearNum is
// null (e.g. TAFE / no listed school) or nothing covers it.
export function resolveCoveringBand<T extends BandLike>(
  bands: T[] | null | undefined,
  yearNum: number | null
): T | null {
  if (yearNum == null) return null;
  const covering = (bands ?? []).filter(
    (b) =>
      !b.deactivated &&
      b.year_from != null &&
      b.year_to != null &&
      b.year_from <= yearNum &&
      b.year_to >= yearNum
  );
  if (!covering.length) return null;
  covering.sort((a, b) => {
    const spanA = (a.year_to as number) - (a.year_from as number);
    const spanB = (b.year_to as number) - (b.year_from as number);
    if (spanA !== spanB) return spanA - spanB; // narrower band first
    const ta = a.byod_checked_at ? Date.parse(a.byod_checked_at) : 0;
    const tb = b.byod_checked_at ? Date.parse(b.byod_checked_at) : 0;
    return tb - ta; // then most recently checked
  });
  return covering[0];
}

// A band is STALE once its policy year is behind the CURRENT policy year — the
// annual mid-December rollover (admin-configurable review month/day). A band with
// no policy year, or no band at all, is treated as not-stale (nothing to re-check).
export function isBandStale(
  band: BandLike | null | undefined,
  now: Date,
  reviewMonth: number,
  reviewDay: number
): boolean {
  if (!band || band.byod_policy_year == null) return false;
  return band.byod_policy_year < policyYearFor(now, reviewMonth, reviewDay);
}
